import { api } from "../scripts/api.js";

/*
   <app-pane-settings> — admin switches for the Excel add-in pane.

   Built 2026-09-21. First switch: the live "Editing" clock (active-time.ts),
   which asks Excel every 5 seconds, while a student is idle, whether a cell edit
   is open. It lives here rather than in a Railway variable so it can be turned
   on for a tester and off again during a live test without a deploy.

   Saved to app_setting via PUT /admin/pane-settings; panes pick it up on their
   next /status ping. The server caches it 15s and panes ping every 30s, so a
   change reaches an open pane in under a minute.

   The server credit for committed edits (up to 5 minutes) does NOT depend on
   this switch; only the live display does.
*/

const esc = (s) =>
   String(s ?? "").replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
   );

class AppPaneSettings extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <div class="card">
            <h2 style="margin:0">Pane settings</h2>
            <p class="muted" style="margin:6px 0 0">
               Switches for the Excel add-in. Changes reach open workbooks within about a minute,
               with no deploy.
            </p>
         </div>
         <div class="card">
            <h3 style="margin:0">Live "Editing" clock</h3>
            <p class="muted" style="margin:6px 0 12px">
               While a student is typing inside a cell, keep the active-time clock counting and show
               <em>Editing: counts when you press Enter</em>. When off, the clock pauses after 30 seconds
               and catches up when they press Enter. Either way, the time is credited once the edit is
               committed (up to 5 minutes).
            </p>
            <div id="ps-state" class="muted" style="margin-bottom:10px">Loading…</div>
            <fieldset id="ps-modes" style="border:0;padding:0;margin:0 0 10px">
               <label style="display:block;margin:4px 0"><input type="radio" name="ps-mode" value="off"> Off for everyone</label>
               <label style="display:block;margin:4px 0"><input type="radio" name="ps-mode" value="testers"> On for these accounts only</label>
               <label style="display:block;margin:4px 0"><input type="radio" name="ps-mode" value="on"> On for everyone</label>
            </fieldset>
            <label for="ps-emails" class="muted" style="display:block;margin-bottom:4px">
               Tester emails, one per line (the account each person signs into the add-in with)
            </label>
            <textarea id="ps-emails" rows="4" style="width:100%;max-width:480px;font:inherit"></textarea>
            <div style="margin-top:12px">
               <button type="button" class="btn-primary" id="ps-save">Save</button>
               <span id="ps-msg" class="msg" style="margin-left:10px"></span>
            </div>
         </div>`;
      this.querySelector("#ps-save").addEventListener("click", () => this.save());
      this.querySelectorAll('input[name="ps-mode"]').forEach((r) =>
         r.addEventListener("change", () => this.syncEmailsEnabled()));
      this.load();
   }

   show() { this.load(); }

   syncEmailsEnabled() {
      const mode = this.querySelector('input[name="ps-mode"]:checked')?.value;
      this.querySelector("#ps-emails").disabled = mode !== "testers";
   }

   msg(text, kind = "") {
      const el = this.querySelector("#ps-msg");
      el.textContent = text;
      el.className = `msg${kind ? " " + kind : ""}`;
   }

   describe(p) {
      if (p.mode === "on") return "Currently <strong>on for everyone</strong>.";
      if (p.mode === "testers") return `Currently <strong>on for ${p.emails.length} tester${p.emails.length === 1 ? "" : "s"}</strong>: ${p.emails.map(esc).join(", ")}.`;
      return "Currently <strong>off</strong>.";
   }

   async load() {
      const state = this.querySelector("#ps-state");
      try {
         const res = await api("/admin/pane-settings");
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || res.status);
         const p = data.editProbe;
         state.innerHTML = data.migrated === false
            ? "The settings table has not been created yet. Run the 2026-09-21 migration first."
            : `${this.describe(p)}${data.updatedAt ? ` Last changed ${esc(data.updatedAt)}.` : ""}`;
         const radio = this.querySelector(`input[name="ps-mode"][value="${p.mode}"]`);
         if (radio) radio.checked = true;
         this.querySelector("#ps-emails").value = (p.emails || []).join("\n");
         this.syncEmailsEnabled();
      } catch (e) {
         state.textContent = `Could not load settings (${e.message}).`;
      }
   }

   async save() {
      const mode = this.querySelector('input[name="ps-mode"]:checked')?.value;
      if (!mode) return this.msg("Pick a setting first.", "err");
      const emails = this.querySelector("#ps-emails").value
         .split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
      this.msg("Saving…");
      try {
         const res = await api("/admin/pane-settings", {
            method: "PUT",
            body: JSON.stringify({ editProbe: { mode, emails } }),
         });
         const data = await res.json();
         if (!res.ok || !data.ok) {
            if (data.reason === "unknown_emails") return this.msg(`No account for: ${data.unknown.join(", ")}`, "err");
            if (data.reason === "no_testers") return this.msg("Add at least one tester email.", "err");
            if (data.reason === "not_migrated") return this.msg("Run the 2026-09-21 migration first.", "err");
            throw new Error(data.reason || res.status);
         }
         this.msg("Saved. Open workbooks pick this up within about a minute.", "ok");
         this.load();
      } catch (e) {
         this.msg(`Save failed (${e.message}).`, "err");
      }
   }
}

customElements.define("app-pane-settings", AppPaneSettings);
